import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../layouts/DashboardLayout';
import HealthPlusActivationModal from '../components/HealthPlusActivationModal';
import PremiumPurchaseModal from '../components/PremiumPurchaseModal';
import HealthMedicalNotice from '../components/HealthMedicalNotice';
import { API_BASE_URL } from '../config';

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}`, ...extra } : extra;
};

export default function HealthPlusHub({ isDarkMode, toggleDarkMode }) {
  const [loading, setLoading] = useState(true);
  const [entitled, setEntitled] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const isActive = useMemo(() => entitled && enabled, [entitled, enabled]);

  const fetchState = async () => {
    try {
      setLoading(true);
      const [profileRes, stateRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/profile`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        }),
        fetch(`${API_BASE_URL}/api/health/activation-state`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        }),
      ]);

      const profileData = await profileRes.json().catch(() => ({}));
      const stateData = await stateRes.json().catch(() => ({}));

      // Faz 5: Backend'in entitled değerine güven (trial guard backend'de — frontend tekrar hesaplamaz).
      const backendEntitled = !!stateData?.entitled;
      setEntitled(backendEntitled);
      setEnabled(backendEntitled ? !!stateData?.enabled : false);
      setSummary(stateData?.summary || null);
      return { entitled: backendEntitled, enabled: backendEntitled ? !!stateData?.enabled : false };
    } catch (error) {
      console.error('health+ state fetch error', error);
      setEntitled(false);
      setEnabled(false);
      return { entitled: false, enabled: false };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchState();
  }, []);

  useEffect(() => {
    const search = new URLSearchParams(window.location.search || '');
    if (search.get('autostart') !== '1') return;
    if (loading) return;
    if (!entitled || !enabled) {
      setShowSetupModal(true);
    }
  }, [loading, entitled, enabled]);

  const setActivationState = async (nextEnabled) => {
    try {
      setBusy(true);
      const res = await fetch(`${API_BASE_URL}/api/health/activation-state`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ enabled: !!nextEnabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'health_activation_state_update_failed');
      setEnabled(!!data.enabled);
    } catch (error) {
      console.error('health+ toggle error', error);
      alert('Sağlık+ durumu güncellenemedi.');
    } finally {
      setBusy(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (isActive) {
      await setActivationState(false);
      return;
    }

    if (!entitled) {
      setShowSetupModal(true);
      return;
    }

    await setActivationState(true);
  };

  const handleProceedToPayment = () => {
    setShowSetupModal(false);
    setShowPaymentModal(true);
  };

  return (
    <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
      <div className="mx-auto w-full max-w-4xl p-4 md:p-6">
        <div className="rounded-3xl border border-[#e6dbdb] bg-background-light p-6 dark:border-gray-800 dark:bg-[#0a0a0a]">
          <div className="mb-3 flex items-center justify-between">
            <h1 className="text-2xl font-black text-gray-900 dark:text-white">Sağlık+</h1>
            <span className={`rounded-full px-3 py-1 text-xs font-black ${isActive ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-white'}`}>
              {isActive ? 'AKTİF' : 'PASİF'}
            </span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Sağlık+ mutfak deneyimini bozmadan önerileri daha uygun şekilde ağırlıklandırır.
          </p>

          <HealthMedicalNotice enabled={isActive} targetMemberKey="household" className="mt-4" />

          {/* Faz 5.4: Aktif sofra sağlık profili özeti */}
          {isActive && summary && (
            <div className="mt-5 rounded-2xl border border-blue-100 bg-blue-50/60 p-4 dark:border-blue-900/40 dark:bg-blue-900/10">
              <p className="text-[11px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-300">
                Bu hafta sofranızın sağlık profili
              </p>
              {Array.isArray(summary.activeModeLabels) && summary.activeModeLabels.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {summary.activeModeLabels.map((label, i) => (
                    <span key={i} className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-blue-700 shadow-sm dark:bg-slate-800 dark:text-blue-200">
                      {label}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Henüz aktif sağlık modu yok. Tahlil ekleyebilir veya tercih belirtebilirsin.
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                {summary.householdMembers > 0 && (
                  <span>👥 {summary.householdMembers} kişilik sofra</span>
                )}
                {summary.compensationLabel && (
                  <span>⚖️ Bugün dengeleme: {summary.compensationLabel}</span>
                )}
                {summary.confidence && (
                  <span>📊 Güven: {summary.confidence === 'high' ? 'Yüksek' : summary.confidence === 'medium' ? 'Orta' : 'Düşük'}</span>
                )}
              </div>
              {summary.doctorReferralRequired && (
                <p className="mt-2 rounded-lg bg-amber-100 px-2.5 py-1.5 text-[11px] font-bold text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  Bazı değerlerin için hekimine danışman önerilir.
                </p>
              )}
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={loading || busy}
              onClick={handlePrimaryAction}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-black text-white disabled:opacity-60"
            >
              {loading ? 'Yükleniyor...' : isActive ? 'Sağlık+ durdur' : entitled ? 'Sağlık+ başlat' : 'Sağlık+ aktivasyonu'}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={() => setShowSetupModal(true)}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 dark:border-slate-700 dark:text-slate-200"
            >
              Ayarları aç
            </button>
          </div>
        </div>
      </div>

      <HealthPlusActivationModal
        isOpen={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        onProceedToPayment={handleProceedToPayment}
      />

      <PremiumPurchaseModal
        isOpen={showPaymentModal}
        onClose={async (reason) => {
          if (reason === 'open') {
            setShowPaymentModal(true);
            return;
          }
          setShowPaymentModal(false);
          const state = await fetchState();
          if (!state?.entitled) return;
          await setActivationState(true);
        }}
        preferredModuleKey="global"
        preferredModuleTitle="Sağlık+ Premium"
      />
    </DashboardLayout>
  );
}
